import Phaser from "phaser";
import {
  ModalLayer,
  NineSliceButton,
  NineSlicePanel,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import type { GridPosition } from "../models/ModuleGrid.js";
import { type SpaceshipDefinition } from "../data/spaceshipData.js";
import { getTextDirection, t } from "../i18n/i18n.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import {
  CONTENT_PANEL_STYLE,
  FONT_FAMILY,
  PRIMARY_BUTTON_STYLE,
  SMALL_BUTTON_STYLE,
  setButtonEnabled,
  TYPO,
} from "./uiTokens.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";
import {
  SPACESHIP_INFO_PANEL_H,
  SPACESHIP_INFO_PANEL_W,
  computeSpaceshipInfoPopupLayout,
} from "./spaceshipInfoPopupLayout.js";
import { spaceshipInfoGridCellPresentation, stripLevelPrefix } from "./spaceshipInfoGridPresentation.js";
import { clampPageIndex, resolveSpaceshipLanePage } from "./spaceshipLanePager.js";

export interface SpaceshipInfoData {
  readonly spaceship: SpaceshipDefinition;
  readonly current: boolean;
  readonly owned: boolean;
  readonly unlockable: boolean;
  readonly unlockedCells: readonly GridPosition[];
}

export interface SpaceshipInfoPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly onEquip: (id: string) => void;
  readonly onClose: () => void;
  readonly onLauncherInfo?: () => void;
}

const PANEL_W = SPACESHIP_INFO_PANEL_W;
const PANEL_H = SPACESHIP_INFO_PANEL_H;
const GRID_CELL = 18;
const GRID_GAP = 4;
const GRID_MAX_W = 128;
const LAUNCHER_COLS = 1;
const GRID_PAGE_VISIBLE_ROWS = 3;
const PAGE_BTN_W = 18;
const PAGE_BTN_H = 18;
const PAGE_LABEL_W = 38;
const PAGE_GAP = 4;
const GRID_TITLE_PAGED_X_OFFSET = 44;
const GRID_PAGER_X_OFFSET = 54;

function cellKey(cell: GridPosition): string {
  return `${cell.x},${cell.y}`;
}

function equipLabel(data: SpaceshipInfoData): string {
  if (data.current) return t("spaceship.detail.equipped");
  if (data.owned) return t("spaceship.detail.equip");
  if (data.unlockable) return t("spaceship.detail.unlockEquip");
  return t("spaceship.detail.locked");
}

/**
 * 우주선 상세 팝업 — 목록 카드를 누르면 정보만 열고, 실제 교체는 하단 장착 버튼으로 실행한다.
 */
export class SpaceshipInfoPopup extends ModalLayer {
  private readonly title: Phaser.GameObjects.Text;
  private readonly image: Phaser.GameObjects.Image;
  private readonly desc: Phaser.GameObjects.Text;
  private readonly flavor: Phaser.GameObjects.Text;
  private readonly stats: Phaser.GameObjects.Text;
  private readonly unlock: Phaser.GameObjects.Text;
  private readonly prevPageButton: NineSliceButton;
  private readonly nextPageButton: NineSliceButton;
  private readonly pageLabel: Phaser.GameObjects.Text;
  private readonly gridTitle: Phaser.GameObjects.Text;
  private readonly equipButton: NineSliceButton;
  private readonly dynamicObjects: Phaser.GameObjects.GameObject[] = [];
  private currentData: SpaceshipInfoData | null = null;
  private pageIndex = 0;
  private pageCount = 1;

  constructor(
    scene: Phaser.Scene,
    private readonly opts: SpaceshipInfoPopupOptions,
  ) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onClose(),
    });

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const layout = computeSpaceshipInfoPopupLayout(cx, cy, GRID_MAX_W, 86);
    const top = layout.panel.y;
    this.setBackdropCloseExclusion(cx - PANEL_W / 2, top, PANEL_W, PANEL_H);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PANEL_W,
      height: PANEL_H,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    this.title = scene.add
      .text(cx, layout.title.y + layout.title.height / 2, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, PANEL_W, 18, getTextDirection()), top + 16, "X", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => this.opts.onClose());

    this.image = scene.add
      .image(cx, layout.image.y + layout.image.height / 2, "spaceship_large")
      .setDisplaySize(layout.image.width, layout.image.height);
    this.desc = scene.add
      .text(cx, layout.desc.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textSecondary,
        align: "center",
        wordWrap: { width: layout.desc.width },
      })
      .setOrigin(0.5, 0);
    this.flavor = scene.add
      .text(cx, layout.flavor.y + layout.flavor.height / 2, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textSecondary,
        fontStyle: "italic",
        align: "center",
        wordWrap: { width: layout.flavor.width },
      })
      .setOrigin(0.5);
    this.stats = scene.add
      .text(cx, layout.projectiles.y + layout.projectiles.height / 2, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.positiveSoft,
        align: "center",
        wordWrap: { width: layout.projectiles.width },
      })
      .setOrigin(0.5);
    this.unlock = scene.add
      .text(cx, layout.unlock.y + layout.unlock.height / 2, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.accentAmber,
        align: "center",
        wordWrap: { width: layout.unlock.width },
      })
      .setOrigin(0.5);
    this.prevPageButton = new NineSliceButton(scene, 0, 0, {
      style: SMALL_BUTTON_STYLE,
      label: "<",
      widthOverride: PAGE_BTN_W,
      heightOverride: PAGE_BTN_H,
    });
    this.prevPageButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.stepPage(-1));
    this.nextPageButton = new NineSliceButton(scene, 0, 0, {
      style: SMALL_BUTTON_STYLE,
      label: ">",
      widthOverride: PAGE_BTN_W,
      heightOverride: PAGE_BTN_H,
    });
    this.nextPageButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.stepPage(1));
    this.pageLabel = scene.add
      .text(0, 0, "1/1", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.micro,
        color: PALETTE.textSecondary,
        align: "center",
      })
      .setOrigin(0.5);
    this.pageLabel.setFixedSize(PAGE_LABEL_W, PAGE_BTN_H);
    this.positionPager(layout);
    this.gridTitle = scene.add
      .text(cx, layout.gridTitle.y + layout.gridTitle.height / 2, t("spaceship.detail.gridTitle"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textPrimary,
      })
      .setOrigin(0.5);

    this.equipButton = new NineSliceButton(scene, cx, layout.equipButton.y + layout.equipButton.height / 2, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("spaceship.detail.equip"),
      widthOverride: layout.equipButton.width,
      heightOverride: layout.equipButton.height,
    });
    this.equipButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
      if (!this.currentData || this.currentData.current || (!this.currentData.owned && !this.currentData.unlockable)) return;
      this.opts.onEquip(this.currentData.spaceship.id);
    });

    this.contentContainer.add([
      this.title,
      closeBtn,
      this.image,
      this.desc,
      this.flavor,
      this.stats,
      this.unlock,
      this.prevPageButton,
      this.pageLabel,
      this.nextPageButton,
      this.gridTitle,
      this.equipButton,
    ]);
  }

  update(data: SpaceshipInfoData): void {
    const previousId = this.currentData?.spaceship.id;
    this.currentData = data;
    const def = data.spaceship;
    if (previousId !== def.id) this.pageIndex = 0;
    this.pageCount = Math.max(1, def.wiringLanes.length);
    this.pageIndex = clampPageIndex(this.pageIndex, this.pageCount);
    this.title.setText(t(def.nameKey));
    this.image.setTexture(def.largeAssetKey);
    this.desc.setText(stripLevelPrefix(t(def.descKey)));
    this.flavor.setText(t(`spaceship.${def.id}.flavor`));
    this.stats.setText(t("spaceship.detail.projectiles", { count: def.projectileMuzzles.length }));
    this.unlock.setText(data.current ? t("spaceship.status.current") : data.owned ? t("spaceship.status.owned") : t(def.unlockCondition.messageKey));
    this.gridTitle.setText(t("spaceship.detail.gridTitle"));
    this.equipButton.setLabel(equipLabel(data));
    setButtonEnabled(this.equipButton, !data.current && (data.owned || data.unlockable));
    this.updatePager();
    this.renderGrid(data);
  }

  private stepPage(delta: number): void {
    if (this.pageCount <= 1 || !this.currentData) return;
    const next = clampPageIndex(this.pageIndex + delta, this.pageCount);
    if (next === this.pageIndex) return;
    this.pageIndex = next;
    this.updatePager();
    this.renderGrid(this.currentData);
  }

  private updatePager(): void {
    const visible = this.pageCount > 1;
    this.pageLabel.setText(`${this.pageIndex + 1}/${this.pageCount}`).setVisible(visible);
    this.prevPageButton.setVisible(visible);
    this.nextPageButton.setVisible(visible);
    setButtonEnabled(this.prevPageButton, visible && this.pageIndex > 0);
    setButtonEnabled(this.nextPageButton, visible && this.pageIndex < this.pageCount - 1);
  }

  private positionPager(layout: ReturnType<typeof computeSpaceshipInfoPopupLayout>): void {
    const cx = layout.gridTitle.x + layout.gridTitle.width / 2 + GRID_PAGER_X_OFFSET;
    const cy = layout.gridTitle.y + layout.gridTitle.height / 2;
    const totalW = PAGE_BTN_W * 2 + PAGE_LABEL_W + PAGE_GAP * 2;
    const left = cx - totalW / 2;
    this.prevPageButton.setPosition(left + PAGE_BTN_W / 2, cy);
    this.pageLabel.setPosition(left + PAGE_BTN_W + PAGE_GAP + PAGE_LABEL_W / 2, cy - PAGE_BTN_H / 2 + 2);
    this.nextPageButton.setPosition(left + PAGE_BTN_W + PAGE_GAP + PAGE_LABEL_W + PAGE_GAP + PAGE_BTN_W / 2, cy);
  }

  private gridTitleX(layout: ReturnType<typeof computeSpaceshipInfoPopupLayout>): number {
    const cx = layout.gridTitle.x + layout.gridTitle.width / 2;
    return this.pageCount > 1 ? cx - GRID_TITLE_PAGED_X_OFFSET : cx;
  }

  private renderGrid(data: SpaceshipInfoData): void {
    for (const obj of this.dynamicObjects) obj.destroy();
    this.dynamicObjects.length = 0;

    const def = data.spaceship;
    const rows = def.gridMap.split("/");
    const unlocked = new Set(data.unlockedCells.map(cellKey));
    const page = resolveSpaceshipLanePage(
      def.gridSize.height,
      def.wiringLanes,
      this.pageIndex,
      def.wiringLanes.length > 1 ? GRID_PAGE_VISIBLE_ROWS : def.gridSize.height,
    );
    const { cell, gridW, gridH, moduleGridOffsetX } = this.gridMetrics(def, page.visibleRows);
    const layout = computeSpaceshipInfoPopupLayout(this.opts.screenWidth / 2, this.opts.screenHeight / 2, gridW, gridH);
    this.positionPager(layout);
    this.gridTitle.setPosition(this.gridTitleX(layout), layout.gridTitle.y + layout.gridTitle.height / 2);
    this.equipButton.setPosition(this.opts.screenWidth / 2, layout.equipButton.y + layout.equipButton.height / 2);
    const startX = layout.grid.x + moduleGridOffsetX;
    const startY = layout.grid.y;
    this.renderLauncherCell(def, page.rowOffset, page.visibleRows, layout.grid.x, startY, cell);

    for (let y = 0; y < rows.length; y++) {
      if (y < page.rowOffset || y >= page.rowOffset + page.visibleRows) continue;
      const row = rows[y]!;
      for (let x = 0; x < row.length; x++) {
        const ch = row[x]!;
        const key = `${x},${y}`;
        const presentation = spaceshipInfoGridCellPresentation(ch, unlocked.has(key));
        if (presentation === "hidden") continue;
        const isDefault = presentation === "defaultUnlocked";
        const isUser = presentation === "userUnlocked";
        const fill = isUser ? PALETTE_NUM.infoBlue : isDefault ? PALETTE_NUM.accentGold : PALETTE_NUM.surfaceDisabled;
        const alpha = isUser || isDefault ? 0.92 : 0.5;
        const stroke = isUser ? PALETTE_NUM.infoBlue : isDefault ? PALETTE_NUM.accentGold : PALETTE_NUM.cellStroke;
        const rx = startX + x * (cell + GRID_GAP);
        const ry = startY + (y - page.rowOffset) * (cell + GRID_GAP);
        const rect = this.scene.add
          .rectangle(rx, ry, cell, cell, fill, alpha)
          .setOrigin(0, 0)
          .setStrokeStyle(1, stroke, 0.9);
        this.contentContainer.add(rect);
        this.dynamicObjects.push(rect);
        if (presentation === "locked") {
          const lock = this.scene.add.graphics();
          lock.lineStyle(1.2, PALETTE_NUM.textSecondary, 0.95);
          lock.strokeRoundedRect(rx + cell * 0.27, ry + cell * 0.45, cell * 0.46, cell * 0.34, 2);
          lock.beginPath();
          lock.arc(rx + cell * 0.5, ry + cell * 0.44, cell * 0.18, Math.PI, 0, false);
          lock.strokePath();
          this.contentContainer.add(lock);
          this.dynamicObjects.push(lock);
        }
      }
    }
  }

  private renderLauncherCell(
    def: SpaceshipDefinition,
    rowOffset: number,
    visibleRows: number,
    gridX: number,
    gridY: number,
    cell: number,
  ): void {
    const lane = def.wiringLanes[this.pageIndex];
    if (!lane) return;
    const localY = lane.start.y - rowOffset;
    if (localY < 0 || localY >= visibleRows) return;
    const rx = gridX;
    const ry = gridY + localY * (cell + GRID_GAP);
    const bg = this.scene.add
      .rectangle(rx, ry, cell, cell, PALETTE_NUM.endpointStart, 0.92)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE_NUM.endpointStart, 0.95)
      .setInteractive({ useHandCursor: true });
    const icon = this.scene.add
      .image(rx + cell / 2, ry + cell / 2, def.launcherIconKey)
      .setDisplaySize(cell * 0.72, cell * 0.72)
      .setInteractive({ useHandCursor: true });
    bg.on("pointerup", () => this.opts.onLauncherInfo?.());
    icon.on("pointerup", () => this.opts.onLauncherInfo?.());
    this.contentContainer.add([bg, icon]);
    this.dynamicObjects.push(bg, icon);
  }

  private gridMetrics(
    def: SpaceshipDefinition,
    visibleRows: number,
  ): {
    readonly cell: number;
    readonly gridW: number;
    readonly gridH: number;
    readonly moduleGridOffsetX: number;
  } {
    const totalCols = def.gridSize.width + LAUNCHER_COLS;
    const totalW = Math.min(GRID_MAX_W, totalCols * GRID_CELL + (totalCols - 1) * GRID_GAP);
    const cell = Math.floor((totalW - (totalCols - 1) * GRID_GAP) / totalCols);
    const gridW = totalCols * cell + (totalCols - 1) * GRID_GAP;
    const gridH = visibleRows * cell + (visibleRows - 1) * GRID_GAP;
    return { cell, gridW, gridH, moduleGridOffsetX: LAUNCHER_COLS * (cell + GRID_GAP) };
  }
}
